<template>
  <div>
    <h3>{{ _('Smart Device') }}</h3>
		<div class="row">
			<div class="col-md-6">
				<div class="form-group">
					<label for="smartdevice" v-if="isNew">{{ _('Please select a Smart Device') }}</label>
					<select class="form-control" id="smartDevice" v-model="link.smartdevice" :disabled="!isNew">
            <optgroup v-for="group in smartdeviceGroups" :label="makeSmartDeviceType({ smartdevice: { subtype: group } })">
              <option v-for="sd in groupedsmartdevices[group]" :value="sd">{{ sd.name }} (ID: {{ sd.id }})</option>
            </optgroup>
					</select>
				</div>
			</div>
		</div>
    <template v-if="link.smartdevice">
      <template v-if="link.smartdevice.type === 'lock' && link.data">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ _('Number of hours to enable PIN code before reservation start') }}</label>
              <input type="number" data-android-inputmode="decimal" class="form-control" v-model.number="link.data.startoffset" />
            </div>
            <div class="form-group">
              <label>{{ _('Number of hours to extend PIN code validity after reservation end') }}</label>
              <input type="number" data-android-inputmode="decimal" class="form-control" v-model.number="link.data.endoffset" />
            </div>
          </div>
          <div class="col-md-6" v-if="!isNew">
            <div class="form-group">
              <label>{{ _('Tags') }}</label>
              <div>
                {{ _('PIN code') }}<br />
                <code>
                  {pin-{{ link.id }}}
                </code>
              </div>
              <div>
                {{ _('PIN code validity') }}<br />
                <code>
                  {pinvalid-{{ link.id }}}
                </code>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-if="link.smartdevice.type === 'temperature'">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ _('Target temperature') }}</label>
              <div class="input-group">
                <input type="number" data-android-inputmode="decimal" class="form-control" v-model.number="link.data.temperature" />
                <div class="input-group-addon">
                  &#8451;
                </div>
              </div>
            </div>
            <div class="form-group">
              <label>{{ _('Set target temperature') }}</label>
              <div class="input-group">
                <input type="number" data-android-inputmode="decimal" class="form-control" v-model.number="link.data.offset" />
                <div class="input-group-btn">
                  <Dropdown buttonClass="btn-default" menuClass="dropdown-menu-right">
                    <template #trigger>
                      {{ timeTypeDesc }} <span class="caret"></span>
                    </template>
                    <li v-for="(timeTypeDesc, timeType) in types"><a :data-value="timeType" href="javascript:;" v-on:click="selectTimeType(timeType)">{{ _(timeTypeDesc) }}</a></li>
                  </Dropdown>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6" v-if="!isNew">
            <div class="form-group">
              <label>{{ _('Tags') }}</label>
              <div>
                {{ _('Temperature') }}<br />
                <code>
                  {temp-{{ link.id }}}
                </code>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
		<div class="flex-row space-between">
      <div class="button-col">
        <button class="btn btn-danger pull-left" type="button" v-on:click="remove" v-if="link && link.smartdevice && link.smartdevice.id"><i class="fa fa-fw fa-trash"></i><span class="hidden-xs"> {{ _('Delete') }}</span></button>
      </div>
      <div class="button-col">
        <button class="btn btn-success" type="button" v-on:click="save" :disabled="!canSave"><i class="fa fa-fw fa-check"></i> {{ _('Ok') }}</button>
        <button class="btn btn-default" type="button" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import errors from '~/lib/errors'
import versions from '~/lib/versions'
import { mapGetters } from 'vuex'

export default {
  mixins: [errors, versions],
  data () {
    return {
      smartdevices: [],
      allsmartdevices: [],
      link: { ...this.modelValue },
      types: {
        'before_reservation_starts': 'hours before the reservation starts',
        'after_reservation_starts': 'hours after the reservation starts',
        'before_reservation_ends': 'hours before the reservation ends',
        'after_reservation_ends': 'hours after the reservation ends'
      },
    }
  },
  props: ['modelValue'],
  computed: {
    ...mapGetters(['shopId', 'baseDomain', 'apiKey']),
    smartdeviceGroups () {
      if (!this.allsmartdevices || !this.allsmartdevices.length) {
        return []
      }
      return [...new Set(this.allsmartdevices.map(sd => sd.subtype))].sort((a, b) => a > b ? 1 : -1)
    },
    groupedsmartdevices () {
      if (!this.allsmartdevices || !this.allsmartdevices.length) {
        return {}
      }
      let grouped = {}
      this.allsmartdevices.forEach(sd => {
        if (!grouped[sd.subtype]) {
          grouped[sd.subtype] = []
        }
        grouped[sd.subtype].push(sd)
      })
      return grouped
    },
    isNew () {
      return this.link && this.link.id && parseInt(this.link.id, 10) != this.link.id
    },
    canSave() {
      if (!this.link) {
        return false
      }
      return !!this.link.smartdevice
    },
    timeType: {
      get () {
        return this.link && this.link.data && this.link.data.timetype ? this.link.data.timetype : 'before_reservation_starts'
      },
      set (newValue) {
        console.log(newValue)
        this.link.data.timetype = newValue
      }
    },
    timeTypeDesc () {
      return this._(this.types[this.link.data.timetype] ? this.types[this.link.data.timetype] : this.types['before_reservation_starts'])
    },
  },
  methods: {
    makeSmartDeviceType (link) {
      if (!link || !link.smartdevice) {
        return ''
      }
      switch (link.smartdevice.subtype) {
        case 'seam':
          return this._('Seam lock')
        case 'seamacs':
          return this._('Seam ACS')
        case 'ratna':
          return this._('Ratna apartment')
        case 'igloo':
          return this._('Igloohome lock')
        case 'yale':
          return this._('Yale lock')
          case 'adax':
          return this._('Adax heater')
      }
      switch (link.smartdevice.type) {
        case 'lock':
          return this._('Lock')
        case 'temperature':
          return this._('Temperature')
      }
      return ''
    },
    selectTimeType(timeType) {
      this.timeType = timeType
    },
    loadAllSmartDevices () {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/smartdevices.json?sorters[]={"field":"name","dir":"asc"}&apikey=' + this.apiKey + this.versionStr)
        } catch(e) {
          this.showError(e)
          reject(e)
          return
        }
        this.allsmartdevices = response.data
        resolve(response.data)
      })
    },
    save () {
      if (!this.link || !this.link.smartdevice) {
        return 
      }
      if (!this.link.smartdevice.id) {
        this.link.smartdevice.id = 'new-' + (new Date().getTime())
      }
      if (this.link.smartdevice.type === 'temperature') {
        if (!this.link.data.timetype) {
          this.link.data.timetype = 'before_reservation_starts'
        }
      }
      this.$emit('update:modelValue', this.link)
    },
    close () {
      this.$emit('close')
    },
    remove() {
      this.$emit('remove', this.link)
    }
  },
  async mounted () {
    await this.loadAllSmartDevices()
  }
}
</script>